import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { FieldNationalId } from '../../../../molecules/fieldNationalId'
import { Field } from '../../../../molecules/field'
import { FieldPassword } from '../../../../molecules/fieldPassword'
import { Button } from '../../../../atoms/button'
import { Text } from '../../../../atoms/text'
import { passwordRegex } from '../../../../../utils/passwordValidation'
import { emailRegex } from '../../../../../utils/emailValidation'
import { doEmailCheck } from '../../../../../adapters/auth'
import { StyledEmailAndPasswordStep } from '../../styles'
import { Link } from 'gatsby'
import useSlugs from '../../../../../hooks/useSlugs'
import { useModalContext } from '../../../../../context/modalProvider'
import { formSubmitIsDisabled } from '../../../../../utils/formUtils'
import useDeviceDetect from '../../../../../hooks/useDeviceDetect'
import { useTranslation } from '../../../../../context/translationProvider'
import { removeCpfMask } from '../../../../../helpers/mask'
import { useTurnstile } from '../../../../../hooks/useTurnstile'
import { brandConfig } from '../../../../../config/brandConfig'
import { FieldDropdown } from '../../../../molecules/fieldDropdown'
import { isFbUser } from '../../../../../helpers/urlHelper'
import { FB_USER_QUERY_PARAM } from '../../../../../utils/constants'
import {
  REGISTRATION_STEP_IDS,
  REGISTRATION_STEPS,
} from '../../../../../hooks/useRegistrationSteps'
import useCAFPolling from '../../customComponents/hooks/useCAFPolling'

const EmailAndPasswordStep = (props) => {
  const {
    formProperties,
    onCompleteStep,
    country,
    setCafData,
    checkNationalId,
    onInputFocus,
    onInputBlur,
    prepopulatedInputs,
    registerTime,
    setToken,
    setStepsDone,
    nationalities,
  } = props
  const { checkCaf } = useCAFPolling({
    formProperties,
    setStepsDone,
    setCafData,
  })
  const [loginSlug] = useSlugs(['login'])
  const [loading, setLoading] = useState(false)
  const fbQuery = isFbUser() ? FB_USER_QUERY_PARAM : ''
  const loginUrl = `${loginSlug}${fbQuery}`
  const { close } = useModalContext()
  const { isMobile } = useDeviceDetect()
  const { translate } = useTranslation()
  const [stepTimer] = useState(new Date())
  const [initialValues, setInitialValues] = useState({})
  const [isCaptchaPassed, setIsCaptchaPassed] = useState(false)
  const { initializeTurnstile, captchaTurnstileOn } = useTurnstile()

  const {
    register,
    getValues,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = formProperties

  const onCaptchaVerified = useCallback((data) => {
    setToken(data)
    setIsCaptchaPassed(true)
  }, [])

  useEffect(() => {
    watch(['national_id', 'email', 'password'])
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'registrationw_cpf_page_viewed',
        nationality: country?.code,
      })
    }
  }, [country?.code])

  initializeTurnstile(onCaptchaVerified)

  // Function to handle the simulated click
  const handleClick = (x, y) => {
    const element = document.elementFromPoint(x, y)
    // Check if the clicked position is really a button
    if (element && element.tagName === 'BUTTON') {
      element.click()
    }
  }

  // Function to capture click events and their positions
  const captureClick = (event) => {
    const { clientX, clientY } = event
    // Call handleClick if not a button to avoid duplicated cliks
    if (event.target.tagName !== 'BUTTON') {
      handleClick(clientX, clientY)
    }
  }

  useEffect(() => {
    const init = async () => {
      if (prepopulatedInputs?.['national_id']) {
        // Set the prepopulated national_id value and check if it's a valid CPF
        setValue('national_id', prepopulatedInputs['national_id'])
        await checkCaf({
          nationalId: removeCpfMask(prepopulatedInputs['national_id']),
        })
        document.querySelector('#email')?.focus()
      }
    }
    init()
    // Add the click event listener to avoid double clicking
    window.addEventListener('click', captureClick)
    // Cleanup the event listener when the component unmounts
    return () => window.removeEventListener('click', captureClick)
  }, [])

  const checkEmail = async () => {
    const stepNum = REGISTRATION_STEPS.findIndex(
      (step) => step.id === REGISTRATION_STEP_IDS.CONFIRM_EMAIL
    )
    setStepsDone((state) => ({ ...state, [stepNum]: false }))

    const emailResponse = await doEmailCheck(getValues('email'))
    if (!emailResponse.ok) {
      setError('email', {
        message: translate(
          'register.error.the_username_already_taken_please_select_another'
        ),
      })
      return false
    }
    return true
  }

  const triggerStepTimer = () => {
    const actualTime = new Date()

    registerTime(
      'first_registration_step',
      actualTime.getTime() - stepTimer.getTime()
    )
  }

  const aditionalChecks = async () => {
    setLoading(true)
    const isSameEmail = initialValues.email === getValues('email')
    const isEmailGood = isSameEmail || (await checkEmail())
    if (isEmailGood) {
      if (!checkNationalId) {
        setLoading(false)

        triggerStepTimer()
        return true
      }
      const isSameCpf = initialValues.national_id === getValues('national_id')
      const isSameNationality =
        initialValues.nationality === getValues('nationality')
      const isCpfGood = (isSameCpf && isSameNationality) || (await checkCaf())
      setLoading(false)

      if (isCpfGood) triggerStepTimer()

      return isCpfGood
    }
    setLoading(false)
    return false
  }

  const onLogin = () => {
    if (location.pathname.includes(`/${loginSlug}/`)) close()
  }

  const completeFirstStep = () => {
    if (!captchaTurnstileOn || isCaptchaPassed) {
      onCompleteStep(['email', 'password', 'national_id'], aditionalChecks)
    }
  }

  useEffect(() => {
    setInitialValues({
      email: getValues('email'),
      national_id: getValues('national_id'),
      nationality: getValues('nationality'),
    })
  }, [])

  return (
    <StyledEmailAndPasswordStep>
      <FieldNationalId
        formProperties={formProperties}
        country={country}
        description={checkNationalId ? translate('register.enterCPF') : ''}
        onFocus={onInputFocus}
        onBlur={() => onInputBlur('national_id_input_timer')}
      />

      <Field
        label={translate('userProfile.email')}
        description={translate('userProfile.emailHint')}
        maxLength="100"
        type="email"
        autoCompleteOff
        showErrorMsg
        errors={errors}
        onFocus={onInputFocus}
        onBlur={() => onInputBlur('email_input_timer')}
        formProperties={register('email', {
          required: true,
          pattern: {
            value: emailRegex,
            message: translate('register.error.incorrectEmailFormat'),
          },
        })}
      />

      <FieldPassword
        errors={errors}
        autoCompleteOff
        description={translate('common.passwordHint')}
        showTipsOnFocus
        scrollOnFocus={isMobile}
        onDone={completeFirstStep}
        onFocus={onInputFocus}
        onBlur={() => onInputBlur('password_input_timer')}
        defaultValue={getValues('password')}
        formProperties={register('password', {
          required: true,
          length: {
            value: 8,
            message: translate('common.passwordHint'),
          },
          pattern: {
            value: passwordRegex,
            message: translate('common.passwordHint'),
          },
        })}
      />

      <FieldDropdown
        defaultValue={' '}
        emptyValue={' '}
        fieldValue={getValues('nationality')}
        autoCompleteOff
        errors={errors}
        label={translate('userProfile.nationality')}
        removeFirstOption={!!getValues('nationality')}
        onFocus={onInputFocus}
        formProperties={register('nationality', { required: true })}
        onBlur={() => onInputBlur('nationality_input_timer')}
        values={nationalities.map((nationality, i) => (
          <option value={nationality.id} key={`nationality-${i}`}>
            {nationality.name}
          </option>
        ))}
      />

      <div className="checkbox">
        <div id="turnstilCaptcha"></div>
      </div>
      <Button
        id="nextBtn1"
        expand
        loading={loading}
        type="button"
        style={{
          fontSize: brandConfig.fonts.text.button.size.desktop,
        }}
        disabled={
          formSubmitIsDisabled(
            ['email', 'password', 'national_id', 'nationality'],
            getValues,
            errors
          ) ||
          (!isCaptchaPassed && captchaTurnstileOn)
        }
        onClick={completeFirstStep}
      >
        {translate('register.next')}
      </Button>

      <Text textAlign="center">
        {translate('register.alreadyHaveAccount')}{' '}
        <Link to={`/${loginUrl}`} onClick={onLogin}>
          {translate('register.login')}
        </Link>
      </Text>
    </StyledEmailAndPasswordStep>
  )
}

EmailAndPasswordStep.propTypes = {
  formProperties: PropTypes.shape({
    register: PropTypes.func,
    getValues: PropTypes.func,
    setError: PropTypes.func,
    setValue: PropTypes.func,
    watch: PropTypes.func,
    formState: PropTypes.shape({ errors: PropTypes.object }),
  }),
  checkNationalId: PropTypes.bool,
  onCompleteStep: PropTypes.func,
  setCafData: PropTypes.func,
  country: PropTypes.object,
  nationalities: PropTypes.array,
  onInputFocus: PropTypes.func,
  onInputBlur: PropTypes.func,
  prepopulatedInputs: PropTypes.object,
  registerTime: PropTypes.func,
  setToken: PropTypes.func,
  setStepsDone: PropTypes.func,
}

export { EmailAndPasswordStep }
